import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { useSeo } from '@/hooks/useSeo';
import { useJsonLd } from '@/hooks/useJsonLd';
import { faqList } from '@/mocks/faq';

const testimonials = [
  { name: '阿栗', role: '妖精粉', text: '入坑星野之森就是从这里的角色百科开始的，资料很全，找声优特别方便。' },
  { name: '小满', role: '声优控', text: '声优表整理得很用心，代表作和角色对照一目了然，省了我好多时间。' },
  { name: '夜行者', role: '资深观众', text: '按作品查角色很高效，人气榜也挺有参考价值，每周都来蹲更新。' },
];

const principles = [
  { icon: 'ri-leaf-line', title: '内容原则', text: '所有角色资料与声优信息力求准确、客观，标注来源与整理口径，避免主观臆断。' },
  { icon: 'ri-compass-line', title: '查找高效', text: '按作品、按分类、按关键词多维度检索，让查角色、找声优成为一件轻松的事。' },
  { icon: 'ri-team-line', title: '开放共建', text: '欢迎每一位爱好者提交纠错与补充，共同把妖精动漫资料馆做得更完整。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 妖精动漫网简介与发展理念',
    description: '了解妖精动漫网：一座专注妖精、精灵类动漫的角色百科与声优表资料馆。认识我们的定位、发展理念、内容原则与用户口碑。',
  });

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于妖精动漫网',
    inLanguage: 'zh-CN',
    description: '专注妖精、精灵类动漫角色百科与声优表的资料馆',
  });

  return (
    <PageLayout>
      <section className="relative overflow-hidden pt-16 md:pt-18">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20fantasy%20forest%20with%20books%20and%20scrolls%20glowing%20with%20emerald%20and%20gold%20magic%20light%2C%20knowledge%20and%20magic%20theme%2C%20soft%20painterly%20atmosphere%2C%20clean%20minimal%20composition&width=1600&height=420&seq=about-banner&orientation=landscape"
          alt="书籍与卷轴散发翠绿金色魔法光芒的奇幻背景"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 to-primary-800/75" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="mb-3 flex items-center gap-1.5 text-xs text-background-100/80">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">关于妖精动漫网</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-6 text-background-100/90">
            一座属于妖精与精灵爱好者的资料馆，专注角色百科与声优表。
          </p>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">我们在做什么</h2>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700">
              妖精动漫网致力于将散落在各处的妖精、精灵类动漫资料集中起来，以「角色百科」与「声优表」两大板块为核心，帮助爱好者快速找到想看的动漫人物介绍、确认某位角色的配音演员，或是从人气榜发现新的心头好。
            </p>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700">
              从森林精灵到星之精灵，从花之妖精到幻兽精灵，我们把每一类角色都整理得清晰有序。无论你是新入坑的观众，还是资深的声优控，都能在这里高效地找到需要的信息。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { num: '12+', label: '收录人气角色' },
              { num: '6', label: '作品专题' },
              { num: '10+', label: '配音演员' },
              { num: '6', label: '妖精分类' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200/70 bg-background-100 p-6 text-center">
                <p className="font-heading text-3xl font-black text-primary-700">{s.num}</p>
                <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="bg-background-100/70 border-y border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">网站定位与发展理念</h2>
            <p className="mt-3 text-sm text-foreground-600">以内容为基石，以查找高效为目标，与爱好者共同成长</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {principles.map((p) => (
              <div key={p.title} className="rounded-xl border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${p.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">用户怎么说</h2>
          <p className="mt-3 text-sm text-foreground-600">来自各位妖精与精灵爱好者的真实反馈</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-xl border border-background-200/70 bg-background-50 p-6">
              <i className="ri-double-quotes-l text-2xl text-accent-400" />
              <blockquote className="mt-3 text-sm leading-7 text-foreground-700">{t.text}</blockquote>
              <figcaption className="mt-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50 font-medium">
                  {t.name[0]}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系方式 + FAQ */}
      <section className="bg-background-100/70 border-t border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm leading-6 text-foreground-600">
                有纠错、建议或合作意向？欢迎通过以下方式与我们联系。我们重视每一条反馈，会认真核实并及时回应。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2.5"><i className="ri-mail-line text-accent-600" /> hi@yaojing-anime.example</li>
                <li className="flex items-center gap-2.5"><i className="ri-time-line text-accent-600" /> 内容每周同步更新</li>
                <li className="flex items-center gap-2.5"><i className="ri-map-pin-line text-accent-600" /> 面向全网妖精动漫爱好者</li>
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">常见问题</h2>
              <div className="space-y-3">
                {faqList.slice(0, 3).map((item) => (
                  <div key={item.question} className="rounded-xl border border-background-200/70 bg-background-50 p-4">
                    <p className="text-sm font-medium text-foreground-950">{item.question}</p>
                    <p className="mt-1.5 text-sm leading-6 text-foreground-600">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}